import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { KeyRound, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email address."); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), base_url: window.location.origin });
      setSent(true);
    } catch (err) { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="h-24 flex items-center px-5 sm:px-8">
        <BrandLogo testId="forgot-logo" />
      </header>
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8" data-testid="forgot-password-card">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
            {sent ? <MailCheck className="w-6 h-6 text-emerald-600" /> : <KeyRound className="w-6 h-6 text-primary" />}
          </div>
          {sent ? (
            <>
              <h1 className="font-heading font-extrabold text-2xl text-slate-900">Check your inbox</h1>
              <p className="font-body text-sm text-slate-500 mt-2" data-testid="forgot-success-msg">
                If an admin account exists for <span className="font-semibold text-slate-700">{email}</span>, we've sent a reset link. It's valid for 1 hour.
              </p>
              <Button asChild variant="outline" className="w-full rounded-xl h-11 font-heading font-bold mt-6" data-testid="forgot-back-btn">
                <Link to="/admin-login"><ArrowLeft className="w-4 h-4 mr-1" /> Back to sign in</Link>
              </Button>
            </>
          ) : (
            <>
              <h1 className="font-heading font-extrabold text-2xl text-slate-900">Forgot your password?</h1>
              <p className="font-body text-sm text-slate-500 mt-2">Enter the email you sign in with and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email" className="font-heading font-bold text-slate-800">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" className="mt-1.5 rounded-xl" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full rounded-xl h-12 font-heading font-bold" data-testid="forgot-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Send reset link
                </Button>
              </form>
              <p className="font-body text-xs text-slate-400 mt-6 text-center">
                <Link to="/admin-login" className="text-primary font-semibold" data-testid="forgot-back-link">Back to sign in</Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
